import type { User } from '@supabase/supabase-js';
import { getUserRole, type Role } from '$lib/db';
import { getSupabase } from '$lib/supabase';

let user = $state<User | null>(null);
let ready = $state(false);
let isAdminUser = $state(false);
/** Role resolved from user_group_memberships via the user_role RPC. */
let resolvedRole = $state<Role | null>(null);
/** Which user id `resolvedRole` belongs to — the readiness gate (D3). */
let resolvedForUserId = $state<string | null>(null);
/** Monotonic token so a slow resolution for a superseded session is discarded. */
let refreshSeq = 0;

function metaIsAdmin(u: User | null): boolean {
  // app_metadata.role is admin-only break-glass: it can restore a locked-out
  // admin via the Supabase dashboard but never grants commercial.
  const meta = u?.app_metadata as Record<string, unknown> | undefined;
  return meta?.role === 'admin' || meta?.is_admin === true;
}

async function refreshRole(u: User | null): Promise<void> {
  const seq = ++refreshSeq;
  if (!u) {
    isAdminUser = false;
    resolvedRole = null;
    resolvedForUserId = null;
    return;
  }
  let role: Role = 'member';
  try {
    role = await getUserRole(u.id);
  } catch {
    role = 'member'; // fail closed
  }
  if (seq !== refreshSeq) return; // superseded by a newer auth change
  resolvedRole = role;
  isAdminUser = role === 'admin';
  resolvedForUserId = u.id;
}

let simulatedRole = $state<'admin' | 'commercial' | 'member' | null>(null);

/** Roles permitted to access the non-admin Access Panel. Extensible for future roles. */
const NON_ADMIN_PANEL_ROLES: Role[] = ['commercial'];

export const auth = {
  get user() {
    return user;
  },
  get ready() {
    return ready;
  },
  /**
   * True once the DB role for the current session has resolved (or there is
   * no session). Nothing may gate on any role getter before this is true —
   * they are all false-ish while unresolved and would bounce an admin whose
   * RPC is still in flight (D3).
   */
  get roleReady(): boolean {
    return !user || (resolvedForUserId !== null && resolvedForUserId === user.id);
  },
  get isPowerhive() {
    return !!this.user?.email?.toLowerCase().endsWith('@powerhive.com');
  },
  get isRealAdmin(): boolean {
    return isAdminUser || metaIsAdmin(user);
  },
  get isRealCommercial(): boolean {
    // D1: read from user_group_memberships only. app_metadata.role==='commercial'
    // is ignored entirely — metadata is break-glass for admin alone.
    return resolvedRole === 'commercial';
  },
  get simulatedRole(): 'admin' | 'commercial' | 'member' | null {
    return simulatedRole;
  },
  setRoleOverride(role: 'admin' | 'commercial' | 'member' | null) {
    simulatedRole = role;
  },
  get isAdmin(): boolean {
    if (simulatedRole) return simulatedRole === 'admin';
    return this.isRealAdmin;
  },
  get isCommercial(): boolean {
    if (simulatedRole) return simulatedRole === 'commercial';
    return this.isRealCommercial;
  },
  get isMember(): boolean {
    return !this.isAdmin && !this.isCommercial;
  },
  get role(): 'admin' | 'commercial' | 'member' {
    if (simulatedRole) return simulatedRole;
    if (this.isAdmin) return 'admin';
    if (this.isCommercial) return 'commercial';
    return 'member';
  },
  /** Admin control panel access: strictly for real/simulated admins. */
  get canAccessAdmin(): boolean {
    return this.isAdmin;
  },
  /** Non-admin access panel: available to commercial users and any future configured roles. */
  get canAccessNonAdminPanel(): boolean {
    return NON_ADMIN_PANEL_ROLES.includes(this.role);
  },
  /** Reading follows the simulated role so an admin previewing Commercial sees what a commercial sees. */
  get canAccessMap(): boolean {
    return this.isAdmin || this.isCommercial;
  },
  get canAccessDashboard(): boolean {
    return this.isAdmin || this.isCommercial;
  },
  get canAccessFleet(): boolean {
    return true;
  },
  get canExport(): boolean {
    return this.isAdmin || this.isCommercial;
  },
  /** Team directory visibility: admins via the Admin Access tab, commercials/allowed roles via the Access panel. */
  get canViewTeam(): boolean {
    return this.isAdmin || this.canAccessNonAdminPanel;
  },
  /** Granting is real-admin only — never reachable through simulation. */
  get canAssignRoles(): boolean {
    return this.isRealAdmin;
  },
  get isSynthetic(): boolean {
    const meta = this.user?.app_metadata as Record<string, unknown> | undefined;
    return (
      meta?.purpose === 'synthetic-performance-test' ||
      meta?.access === 'read-only' ||
      this.user?.email?.toLowerCase() === 'perf-bot@powerhive.com'
    );
  },
  get avatarUrl(): string | undefined {
    const md = this.user?.user_metadata as Record<string, unknown> | undefined;
    const a = typeof md?.avatar_url === 'string' ? md.avatar_url : undefined;
    const p = typeof md?.picture === 'string' ? md.picture : undefined;
    return a || p;
  },
  get initials(): string {
    const email = this.user?.email ?? '';
    return email.slice(0, 1).toUpperCase();
  },

  /** Re-resolve the DB role for the current session (used after sign-in/out; exposed for manual refresh). */
  refreshRole(u: User | null): Promise<void> {
    return refreshRole(u);
  },

  /** Load the existing session and subscribe to changes. */
  async init(): Promise<void> {
    const sb = getSupabase();
    if (!sb) {
      ready = true;
      return;
    }
    const { data } = await sb.auth.getSession();
    user = data.session?.user ?? null;
    await refreshRole(user);
    ready = true;
    sb.auth.onAuthStateChange(async (_event, session) => {
      user = session?.user ?? null;
      await refreshRole(user);
    });
  },

  async signInWithEmail(email: string): Promise<{ error: string | null }> {
    const sb = getSupabase();
    if (!sb) return { error: 'Not connected' };
    const { error } = await sb.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    return { error: error?.message ?? null };
  },

  async signInWithGoogle(): Promise<{ error: string | null }> {
    const sb = getSupabase();
    if (!sb) return { error: 'Not connected' };
    const { error } = await sb.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
        queryParams: { hd: 'powerhive.com', prompt: 'select_account' },
      },
    });
    return { error: error?.message ?? null };
  },

  async signOut(): Promise<void> {
    const sb = getSupabase();
    await sb?.auth.signOut();
  },
};
